import { LatLng } from "leaflet";
import React, { createContext, useContext, useMemo, useState } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import {
  OverlapMarker,
  spreadMarkerPoints,
} from "../shared/markerOverlap";

export type { OverlapMarker } from "../shared/markerOverlap";

export const sensorOverlapId = (type: string, id: string) =>
  `sensor:${type}:${id}`;
export const cameraOverlapId = (url: string) => `camera:${url}`;

const MarkerOverlapContext = createContext<ReadonlyMap<string, LatLng>>(
  new Map()
);

export const MarkerOverlapProvider = ({
  markers,
  children,
}: {
  markers: OverlapMarker[];
  children: React.ReactNode;
}) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({
    zoomend: (event) => setZoom(event.target.getZoom()),
  });
  const positions = useMemo(() => {
    const uniqueMarkers = Array.from(
      new Map(markers.map((marker) => [marker.id, marker])).values()
    );
    const projected = uniqueMarkers.map((marker) => ({
      ...marker,
      point: map.project(marker.position, zoom),
    }));
    return new Map(
      Array.from(spreadMarkerPoints(projected), ([id, point]) => [
        id,
        map.unproject(point, zoom),
      ])
    );
  }, [map, markers, zoom]);

  return (
    <MarkerOverlapContext.Provider value={positions}>
      {children}
    </MarkerOverlapContext.Provider>
  );
};

export const useMarkerPosition = (id: string, position: LatLng) =>
  useMarkerPositions().get(id) || position;

export const useMarkerPositions = () => useContext(MarkerOverlapContext);